import { useState, useEffect } from 'react';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { MdDelete, MdCheckCircle, MdClose } from 'react-icons/md';
import { formatDate } from '../utils/format';

export default function Moderation() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  // Delete modal state
  const [deleteModal, setDeleteModal] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchReports = async () => {
    try {
      const res = await api.get('/admin/comments/reports');
      setReports(res.data.data);
    } catch (err) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReports(); }, []);

  const confirmDelete = async () => {
    if (!deleteModal) return;
    const targetId = deleteModal.comment_id || deleteModal.id;
    const reportId = deleteModal.id;
    setDeleting(true);
    try {
      await api.delete(`/admin/comments/${targetId}`);
      toast.success('Reported item deleted successfully.');
      
      // Instantly remove item from local state list
      setReports(prev => prev.filter(r => r.id !== reportId && r.comment_id !== targetId));
      setDeleteModal(null);
      fetchReports();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete reported item.');
    } finally {
      setDeleting(false);
    }
  };

  const dismissReport = async (reportId) => {
    try {
      await api.patch(`/admin/comments/reports/${reportId}/resolve`);
      toast.success('Report dismissed');
      setReports(prev => prev.filter(r => r.id !== reportId));
      fetchReports();
    } catch (err) {
      toast.error('Failed to dismiss report');
    }
  };

  if (loading) return <div className="page-loader">Loading...</div>;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h2 style={{ margin: '0 0 4px 0' }}>Moderation</h2>
          <p style={{ margin: 0, color: '#6B7280', fontSize: '14px' }}>{reports.length} pending reviews</p>
        </div>
      </div>

      {reports.length === 0 ? (
        <div className="empty-state">
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>✅</div>
          <h3>All clear!</h3>
          <p>No reported comments to review.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {reports.map(report => (
            <div key={report.id} className="card" style={{ padding: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                <div>
                  <span className="badge-expired" style={{ display: 'inline-flex' }}>🚨 Reported</span>
                </div>
                {(report.article_title || report.Comment?.Article?.title) && (
                  <span style={{ fontSize: '13px', color: '#6B7280', maxWidth: '60%', textAlign: 'right' }}>
                    on: {report.article_title || report.Comment?.Article?.title}
                  </span>
                )}
              </div>
              <p style={{ margin: '12px 0', color: '#374151', fontSize: '14px', lineHeight: '1.5', fontStyle: 'italic' }}>
                "{report.comment_body || report.Comment?.body}"
              </p>
              {report.reason && (
                <p style={{ margin: '8px 0', color: '#6B7280', fontSize: '13px' }}>
                  <strong>Reason:</strong> {report.reason}
                </p>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #E5E7EB' }}>
                <span style={{ fontSize: '12px', color: '#9CA3AF' }}>{formatDate(report.created_at)}</span>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button className="btn btn-danger" onClick={() => setDeleteModal(report)}>
                    <MdDelete /> Delete
                  </button>
                  <button className="btn btn-ghost" onClick={() => dismissReport(report.id)}>
                    <MdCheckCircle /> Dismiss
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal && (
        <div className="modal-overlay" onClick={() => setDeleteModal(null)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete Comment</h3>
              <button className="modal-close" onClick={() => setDeleteModal(null)}><MdClose /></button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this comment?</p>
              <p className="text-muted" style={{ marginTop: 8 }}>"{deleteModal.comment_body || deleteModal.body}"</p>
              <p className="text-muted" style={{ marginTop: 8 }}>This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setDeleteModal(null)}>Cancel</button>
              <button className="btn btn-danger-solid" onClick={confirmDelete} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
